import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts"
import locale from 'antd/locale/sv_SE';
import { Button, DatePicker, Space, ConfigProvider } from "antd"
import { useParams, useNavigate } from "react-router-dom"
import { fetchSensorLog } from "../api/Cards"
import { useState, useRef, useEffect } from "react"
import { useUser } from "../contexts/UserContext"
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import * as isLeapYear from 'dayjs/plugin/isLeapYear'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { scaleLinear } from "d3-scale";
import "../SensorGraph.css"

const { RangePicker } = DatePicker;


const SensorGraph = () => {

    let { id, sensor } = useParams()
    const { getToken } = useUser()
    const sensorLogs = useRef([]);
    const defaultEndDate = useRef(null)
    const defaultStartDate = useRef(null)
    const currentDateRange = useRef([])
    const [graphData, setGraphData] = useState([])
    const navigate = useNavigate()
    const dynamicDomain = useRef([0, 1])
    dayjs.locale('sv-se');
    dayjs.extend(isLeapYear)
    dayjs.extend(customParseFormat);
    const scale = scaleLinear();

    id = id.replaceAll('!', '/');

    useEffect(() => {
        async function getSensorLogs() {

            var data = await fetchSensorLog({ Card: { Token: getToken(), CardID: id, CardIndex: 0 }, SensorID: sensor })
            if (typeof data === "number") {
                console.log("Disconnect: ", data)
                navigate('/dashboard');
                //return;
            }
            data = data.filter((sample) => sample.TS < 100000000);
            data = data.sort(function (a, b) { return a.TS - b.TS });

            //for (var i = 0; i < 100; ++i) {
            //    console.log(getTimeDate(data[i].TS))
            //}



            sensorLogs.current = data

            defaultStartDate.current = getDefaultStartDate();
            defaultEndDate.current = getDefaultEndDate();

            //console.log("DefaultStartDate: " + defaultStartDate.current.toString());
            //console.log("DefaultStopDate: " + defaultEndDate.current.toString());

            currentDateRange.current = [defaultStartDate.current, defaultEndDate.current]

            //console.log("Start date: " + defaultStartDate.current.format("DD"))
            //console.log("End date: " + defaultEndDate.current.format("DD"))

            renderGraph(defaultStartDate.current, defaultEndDate.current)
        }

        getSensorLogs();
    }, [])

    //When data has arrived


    function isDST(d) {
        let jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
        let jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
        return Math.max(jan, jul) !== d.getTimezoneOffset();
    }

    function getTimeDate(Minutes) {
        var d = new Date("2024-01-01 01:00:00") 
        var DSTOffset = 0;
        //if (isDST(new Date()))
        //    DSTOffset = 3600;

        //console.log("Offset " + DSTOffset)

        //Fall back time is default, so no offset.
        //Summer time adds an extra hour
        //TODO: Add to universally accessible file instead of making multiple copies. See @events.js
        d.setUTCSeconds((Minutes * 60) - DSTOffset)
        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        };
        return d.toLocaleDateString("sv-SE", options)
    }

    function getTimestampFromDate(TimeDateString) {
        var d = new Date(TimeDateString)
        var DSTOffset = 0;
        if (isDST(new Date()))
            DSTOffset = 3600;
        d.setSeconds(d.getSeconds() + DSTOffset)


        var epoch = new Date("2024-01-01 00:00:00")
        var deltaseconds = d.getTime() - epoch.getTime();
        return (deltaseconds / 1000) / 60;
    }

    function getTimeDateObject(Minutes) {
        var d = new Date("2024-01-01 24:00:00")
        var DSTOffset = 0;
        if (isDST(new Date()))
            DSTOffset = 3600;

        //console.log("Offset " + DSTOffset)

        //Fall back time is default, so no offset.
        //Summer time adds an extra hour
        //TODO: Add to universally accessible file instead of making multiple copies. See @events.js
        d.setUTCSeconds((Minutes * 60) - DSTOffset)
        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        };
        return d
    }

    function getCurrentDate() {
        var d = new Date() 

        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        };
        return d.toLocaleDateString("sv-SE", options)
    }

    function getDefaultStartDate() {
        if (sensorLogs.current.length == 0)
            return dayjs(getCurrentDate(), "YYYY-MM-DD")

        return dayjs(getTimeDate(sensorLogs.current[0].TS), "YYYY-MM-DD")
    }

    function getDefaultEndDate() {
        if (sensorLogs.current.length == 0)
            return dayjs(getCurrentDate(), "YYYY-MM-DD")

        return dayjs(getTimeDate(sensorLogs.current[sensorLogs.current.length - 1].TS), "YYYY-MM-DD")
    }

    //This gets called once for every day/month/year in the display field
    function disableDate(current, { from, type }) {

        switch (type) {
            case 'year':
                if (current.year() < defaultStartDate.current.year())
                    return true;
                else if (current.year() > defaultEndDate.current.year())
                    return true;
                break;
            case 'month':
                if (current.month() < defaultStartDate.current.month())
                    return true;
                else if (current.month() > defaultEndDate.current.month())
                    return true;
                break;
            default:
                if (current < defaultStartDate.current)
                    return true;
                else if (current > defaultEndDate.current)
                    return true;
                break;
        }

        return false;

    }

    function dateChanged(jsdayobj, strings) {
        renderGraph(jsdayobj[0], jsdayobj[1])
    }

    function renderGraph(start, end) {
        //Create a new array that can be displayed in the graph
        //Filter out irrelevant dates first
        const startTime = getTimestampFromDate(start.format("YYYY-MM-DD HH:mm"))
        //All the date values are rounded down, but when we select the end date, we want to include that day
        //So we must add 24 hours to get the correct timestamp
        const stopTime = getTimestampFromDate(end.add(24, 'hour').format("YYYY-MM-DD HH:mm"))   
        //console.log("Start " + startTime);
        //console.log("Stop " + stopTime);
        dynamicDomain.current = [0, 0]

        

        var data = sensorLogs.current.filter(function (value) {
            if (dynamicDomain.current[0] == 0 || dynamicDomain.current[0] > value.Min)
                dynamicDomain.current[0] = value.Min;

            if (value.Max > dynamicDomain.current[1])
                dynamicDomain.current[1] = value.Max;

            return value.TS >= startTime && value.TS <= stopTime
        });

        //console.log("Start: " + startTime + " Stop: " + stopTime + " Num " + data.length);

        //Rescale
        //0 == min
        //1 == max
        const range = dynamicDomain.current[1] - dynamicDomain.current[0]
        dynamicDomain.current[1] += (range * 0.1);  //Add 10% to the top


        //console.log(data)
        var finaldata = data.map((entry, i) => ({ date: getTimeDate(entry.TS), min: entry.Min / 10.0, max: entry.Max / 10.0, avg: entry.Avg / 10.0 }))

        setGraphData(finaldata)
    }

    const dateFormat = "YYYY-MM-DD"

    return(
    <div style={{ width: "100%", height: "700px", margin: "auto", paddingBottom: "20px" }}>
            <div style={{ display: "block", width: "100%", height: "50px" }}>
                <ConfigProvider locale={locale}>
                    <RangePicker style={{left:"60px"}} defaultValue={[defaultStartDate.current, defaultEndDate.current]} format={dateFormat} onChange={dateChanged} disabledDate={disableDate} />
                </ConfigProvider>
        </div>
        

            <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={graphData} margin={{ top: 35, right: 5, bottom: 5, width: "100%", height: "100%" }}>
                    <defs>
                        <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#824a9d" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#824a9d" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorMax" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorMin" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#a3d1ef" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#a3d1ef" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#dedede" />
                    <XAxis dataKey="date" stroke="#333" />
                    <YAxis type="number" stroke="#333" domain={([dataMin, dataMax]) => { return [(dataMin - ((dataMax - dataMin) * 0.1)).toFixed(1), (dataMax + ((dataMax - dataMin) * 0.1)).toFixed(1)]; }}  />
                    <Tooltip wrapperStyle={{ borderColor: "white", boxShadow: "2px 2px 3px rgba(0,0,0,0.3)" }} />

                    <Area type="monotone" dataKey="max" stroke="#82ca9d" fillOpacity={1} fill="url(#colorMax)" />
                    <Area type="monotone" dataKey="avg" stroke="#824a9d" fillOpacity={1} fill="url(#colorAvg)" />
                    <Area type="monotone" dataKey="min" stroke="#8884d8" fillOpacity={1} fill="url(#colorMin)" />
                    
                </AreaChart>
            </ResponsiveContainer>
    </div>)
}
   
//[dynamicDomain.current[0] / 10.0, dynamicDomain.current[1] / 10.0]
export default SensorGraph
